import { BACKEND_URL } from '../../../config/backendConfig';
import { AUTH_CLIENT, AUTH_ERROR_CODE, AUTH_FLOW } from '../constants';

export class AuthApiError extends Error {
  /**
   * @param {string} message
   * @param {string} code
   * @param {number} [status] - HTTP status; undefined for a network failure
   */
  constructor(message, code, status) {
    super(message);
    this.name = 'AuthApiError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Map a failed auth response onto a stable error code.
 *
 * @param {number} status
 * @param {{ code?: string, error?: string }} body
 * @returns {string}
 */
export function authErrorCodeFromResponse(status, body = {}) {
  if (body.code && Object.values(AUTH_ERROR_CODE).includes(body.code)) {
    return body.code;
  }

  const message = String(body.error || '').toLowerCase();
  if (status === 429 || message.includes('too many')) {
    return AUTH_ERROR_CODE.TOO_MANY_ATTEMPTS;
  }
  if (message.includes('expired')) {
    return AUTH_ERROR_CODE.OTP_EXPIRED;
  }
  if (message.includes('invalid otp') || message.includes('no pending otp')) {
    return AUTH_ERROR_CODE.INVALID_OTP;
  }
  if (message.includes('phone')) {
    return AUTH_ERROR_CODE.INVALID_PHONE;
  }
  if (status === 401 || status === 403) {
    return AUTH_ERROR_CODE.UNAUTHORIZED;
  }
  return AUTH_ERROR_CODE.REQUEST_FAILED;
}

/**
 * Phone login, OTP check, session refresh, and logout.
 * Callers pass an already-validated phone and OTP.
 */
export class AuthApi {
  /**
   * @param {{ fetchImpl?: typeof fetch, baseUrl?: string }} [options]
   */
  constructor({ fetchImpl, baseUrl } = {}) {
    this.fetchImpl = fetchImpl || ((...args) => fetch(...args));
    this.baseUrl = baseUrl || BACKEND_URL;
  }

  /** Send a login OTP to a 10-digit phone. Channel defaults to SMS. */
  login(phone, channel) {
    const body = { phone };
    if (channel) body.channel = channel;
    return this.post('/api/auth/send-login-otp', body);
  }

  /** Exchange a phone OTP for a session, a signup ticket, or a social link result. */
  verifyOtp(phone, otp, socialTicket) {
    const body = { phone, otp, client: AUTH_CLIENT, flow: AUTH_FLOW };
    if (socialTicket) body.socialTicket = socialTicket;
    return this.post('/api/auth/verify-login-otp', body);
  }

  verify(accessToken) {
    return this.get('/api/auth/verify', accessToken);
  }

  google(accessToken) {
    return this.post('/api/auth/google', { access_token: accessToken, client: AUTH_CLIENT, flow: AUTH_FLOW });
  }

  apple(identityToken, fullName) {
    const body = { identityToken, client: AUTH_CLIENT, flow: AUTH_FLOW };
    if (fullName && (fullName.firstName || fullName.lastName)) {
      body.fullName = fullName;
    }
    return this.post('/api/auth/apple', body);
  }

  completePhoneSignup({ signupTicket, firstName, email }) {
    return this.post('/api/auth/complete-phone-signup', {
      signupTicket,
      firstName,
      email: email || undefined,
      client: AUTH_CLIENT,
      flow: AUTH_FLOW,
    });
  }

  addPhone(phone, accessToken, channel) {
    const body = { phone, flow: AUTH_FLOW };
    if (channel) body.channel = channel;
    return this.post('/api/auth/add-phone', body, accessToken);
  }

  verifyPhone(phone, otp, accessToken) {
    return this.post('/api/auth/verify-phone', { phone, otp, flow: AUTH_FLOW }, accessToken);
  }

  /** Email a 6-digit code to confirm the logged-in user's address. */
  sendEmailOtp(email, accessToken) {
    return this.post('/api/auth/email/send-otp', { email }, accessToken);
  }

  /** Check the emailed code; on success the server saves the email as verified. */
  verifyEmailOtp(email, otp, accessToken) {
    return this.post('/api/auth/email/verify-otp', { email, otp }, accessToken);
  }

  /** Send a code to the NEW mobile before it replaces the logged-in user's number. Channel defaults to SMS. */
  sendPhoneChangeOtp(phone, accessToken, channel) {
    const body = { phone };
    if (channel) body.channel = channel;
    return this.post('/api/auth/phone/change/send-otp', body, accessToken);
  }

  /** Check the code and save the new mobile. Returns a fresh session: other devices are signed out. */
  verifyPhoneChange(phone, otp, accessToken) {
    return this.post('/api/auth/phone/change/verify', { phone, otp, client: AUTH_CLIENT }, accessToken);
  }

  /** Exchange a refresh token for a new access token. */
  refreshToken(refreshToken) {
    return this.post('/api/auth/refresh', { refreshToken, client: AUTH_CLIENT });
  }

  /**
   * Best-effort server logout. Network and HTTP failures do not throw.
   *
   * @param {{ refreshToken?: string, accessToken?: string, allDevices?: boolean }} [session]
   */
  logout({ refreshToken, accessToken, allDevices = false } = {}) {
    return this.post(
      '/api/auth/logout',
      { refreshToken: refreshToken || undefined, allDevices },
      accessToken,
      { swallow: true },
    );
  }

  /**
   * @param {string} path
   * @param {object} body
   * @param {string} [accessToken]
   * @param {{ swallow?: boolean }} [options]
   */
  async get(path, accessToken) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      const response = await this.fetchImpl(`${this.baseUrl}${path}`, { method: 'GET', headers });
      const data = await readJson(response);
      if (!response.ok) {
        throw new AuthApiError(
          data.error || 'Request failed',
          authErrorCodeFromResponse(response.status, data),
          response.status,
        );
      }
      return data;
    } catch (error) {
      if (error instanceof AuthApiError) throw error;
      throw new AuthApiError(
        error && error.message ? error.message : 'Request failed',
        AUTH_ERROR_CODE.REQUEST_FAILED,
      );
    }
  }

  async post(path, body, accessToken, { swallow = false } = {}) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const data = await readJson(response);
      if (!response.ok) {
        if (swallow) return data;
        throw new AuthApiError(
          data.error || 'Request failed',
          authErrorCodeFromResponse(response.status, data),
          response.status,
        );
      }
      return data;
    } catch (error) {
      if (swallow) return { success: false };
      if (error instanceof AuthApiError) throw error;
      throw new AuthApiError(
        error && error.message ? error.message : 'Request failed',
        AUTH_ERROR_CODE.REQUEST_FAILED,
      );
    }
  }
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

export const authApi = new AuthApi();

/** Used by the shared API client to renew an access token. */
export function refreshSession(refreshToken) {
  return authApi.refreshToken(refreshToken);
}
